import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCustomer } from '../../context/CustomerContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Package, RotateCcw, Shield, ArrowRight, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountOverview = () => {
  const { customer, getAuthHeaders } = useCustomer();
  const [recentOrders, setRecentOrders] = useState([]);
  const [stats, setStats] = useState({ orders: 0, returns: 0, warranties: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [ordersRes, returnsRes, warrantiesRes] = await Promise.all([
        axios.get(`${API}/customer/orders?limit=3`, { headers }),
        axios.get(`${API}/customer/returns?limit=1`, { headers }),
        axios.get(`${API}/warranty?limit=1`, { headers })
      ]);

      setRecentOrders(ordersRes.data.orders || []);
      setStats({
        orders: ordersRes.data.total || 0,
        returns: returnsRes.data.total || 0,
        warranties: warrantiesRes.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      return_requested: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-overview">
      {/* Welcome Card */}
      <Card className="bg-gradient-to-r from-amber-50 to-stone-50 border-amber-200">
        <CardContent className="p-6">
          <h2 className="text-2xl font-serif text-stone-800">
            Welcome back, {customer?.first_name}!
          </h2>
          <p className="text-stone-600 mt-1">
            Manage your orders, returns, and warranty registrations all in one place.
          </p>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.orders}</p>
              <p className="text-stone-600 text-sm">Total Orders</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <RotateCcw className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.returns}</p>
              <p className="text-stone-600 text-sm">Returns</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <Shield className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stats.warranties}</p>
              <p className="text-stone-600 text-sm">Warranties</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-serif">Recent Orders</CardTitle>
          <Link to="/account/orders">
            <Button variant="ghost" size="sm" className="text-amber-700">
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentOrders.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No orders yet</p>
              <Link to="/shop/all">
                <Button variant="link" className="text-amber-700 mt-2">
                  Start Shopping
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {recentOrders.map((order) => (
                <div 
                  key={order.id}
                  className="flex items-center justify-between p-4 bg-stone-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-stone-800">{order.order_number}</p>
                    <div className="flex items-center gap-2 mt-1 text-sm text-stone-500">
                      <Clock className="h-4 w-4" />
                      {new Date(order.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={getStatusColor(order.order_status)}>
                      {order.order_status?.replace('_', ' ')}
                    </Badge>
                    <p className="font-semibold text-stone-800">
                      ${order.total?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <Link to="/account/warranty">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Shield className="h-6 w-6 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-800">Register Warranty</h3>
                <p className="text-sm text-stone-500">Protect your purchase</p>
              </div>
              <ArrowRight className="h-5 w-5 ml-auto text-stone-400" />
            </CardContent>
          </Link>
        </Card>
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <Link to="/account/returns">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <RotateCcw className="h-6 w-6 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-800">Start a Return</h3>
                <p className="text-sm text-stone-500">Easy returns within 30 days</p>
              </div>
              <ArrowRight className="h-5 w-5 ml-auto text-stone-400" />
            </CardContent>
          </Link>
        </Card>
      </div>
    </div>
  );
};

export default AccountOverview;
